import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Check, Copy, Mail } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { BookingPeriodDateFields } from "@/components/ui/BookingPeriodDateFields";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/apiClient";
import {
  addDaysLocal,
  countNightsLocal,
  dateValidationMessage,
  formatDateTimeShort,
  formatIsoDateShort,
  todayLocal,
  validateBookingDates,
} from "@/lib/bookingDates";
import { useArrivalTypes } from "@/lib/useArrivalTypes";
import { DEFAULT_PITCH_CATEGORY_COLOR } from "@/lib/pitchCategoryColors";
import type { MailReplySeed } from "@/lib/mailReservationReply";
import type { Booking, PitchCategory } from "@/types/camping";

const inputClass = "eh-input";

const HOLD_PRESETS = [
  { id: "24h", label: "24 Stunden", hours: 24 },
  { id: "48h", label: "2 Tage", hours: 48 },
  { id: "7d", label: "7 Tage", hours: 24 * 7 },
] as const;

type HoldPresetId = (typeof HOLD_PRESETS)[number]["id"] | "custom";

export interface PhoneBookingHoldResult {
  booking: Booking;
  completion_url: string;
  completion_url_path: string;
  hold_until: string;
  email_sent: boolean;
}

export interface PhoneBookingHoldModalProps {
  open: boolean;
  onClose: () => void;
  categories: PitchCategory[];
  onCreated: (result: PhoneBookingHoldResult) => void;
  /** Vorbefüllung aus einer eingegangenen E-Mail. */
  seed?: MailReplySeed | null;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function toLocalDateTimeInput(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function holdLocalFromHours(hours: number): string {
  return toLocalDateTimeInput(new Date(Date.now() + hours * 60 * 60 * 1000));
}

function localDateTimeToIso(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    throw new Error("Ungültiges Datum/Uhrzeit.");
  }
  return d.toISOString();
}

export function PhoneBookingHoldModal({
  open,
  onClose,
  categories,
  onCreated,
  seed = null,
}: PhoneBookingHoldModalProps) {
  const { types: arrivalTypes, loading: arrivalTypesLoading, error: arrivalTypesError } =
    useArrivalTypes();
  const [arrivalDate, setArrivalDate] = useState(todayLocal());
  const [departureDate, setDepartureDate] = useState(addDaysLocal(todayLocal(), 2));
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [arrivalType, setArrivalType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [dogs, setDogs] = useState(0);
  const [wantsElectricity, setWantsElectricity] = useState(false);
  const [holdPreset, setHoldPreset] = useState<HoldPresetId>("48h");
  const [holdUntilLocal, setHoldUntilLocal] = useState(() => holdLocalFromHours(48));
  const [notes, setNotes] = useState("");
  const [guestId, setGuestId] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdResult, setCreatedResult] = useState<PhoneBookingHoldResult | null>(null);
  const [copied, setCopied] = useState(false);

  const activeCategories = useMemo(
    () =>
      [...categories]
        .filter((c) => c.is_active !== false)
        .sort((a, b) => a.sort_order - b.sort_order),
    [categories],
  );

  const nights = countNightsLocal(arrivalDate, departureDate);
  const selectedCategory = activeCategories.find((c) => c.id === categoryId) ?? null;

  function resetForm(nextSeed: MailReplySeed | null) {
    const arrival = nextSeed?.arrivalDate || todayLocal();
    const departure = nextSeed?.departureDate || addDaysLocal(arrival, 2);
    setArrivalDate(arrival);
    setDepartureDate(departure);
    setFirstName(nextSeed?.firstName ?? "");
    setLastName(nextSeed?.lastName ?? "");
    setEmail(nextSeed?.email ?? "");
    setArrivalType(nextSeed?.arrivalType ?? "");
    setCategoryId(nextSeed?.categoryId ?? "");
    setAdults(nextSeed?.adults ?? 2);
    setChildren(nextSeed?.children ?? 0);
    setDogs(nextSeed?.dogs ?? 0);
    setWantsElectricity(nextSeed?.wantsElectricity ?? false);
    setGuestId(nextSeed?.guestId ?? "");
    setHoldPreset("48h");
    setHoldUntilLocal(holdLocalFromHours(48));
    setNotes(nextSeed?.notes ?? "");
    setShowNotes(Boolean(nextSeed?.notes));
    setError(nextSeed?.placeBanned ? "Für diesen Gast besteht ein Platzverbot." : null);
    setCreatedResult(null);
    setCopied(false);
    setSubmitting(false);
  }

  useEffect(() => {
    if (!open) return;
    resetForm(seed);
    // Formular bei jedem Öffnen neu aufsetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed]);

  function selectHoldPreset(id: HoldPresetId) {
    setHoldPreset(id);
    const preset = HOLD_PRESETS.find((item) => item.id === id);
    if (preset) {
      setHoldUntilLocal(holdLocalFromHours(preset.hours));
    }
    setError(null);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const dateError = dateValidationMessage(validateBookingDates(arrivalDate, departureDate));
    if (dateError) {
      setError(dateError);
      return;
    }
    if (!email.trim()) {
      setError("Bitte die E-Mail-Adresse angeben, an die der Link gehen soll.");
      return;
    }
    let holdUntilIso: string;
    try {
      holdUntilIso = localDateTimeToIso(holdUntilLocal);
    } catch {
      setError("Ungültige Reservierungsfrist.");
      return;
    }
    if (new Date(holdUntilIso).getTime() <= Date.now()) {
      setError("Die Reservierungsfrist muss in der Zukunft liegen.");
      return;
    }
    if (seed?.placeBanned) {
      setError("Für diesen Gast besteht ein Platzverbot. Eine Reservierung wird nicht angelegt.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await api.post<PhoneBookingHoldResult>("/api/v1/bookings/phone-holds", {
        arrival_date: arrivalDate,
        departure_date: departureDate,
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim(),
        arrival_type: arrivalType || null,
        pitch_category_id: categoryId || null,
        adults,
        children,
        dogs,
        wants_electricity: wantsElectricity,
        guest_id: guestId || null,
        hold_until: holdUntilIso,
        notes: notes.trim() || null,
      });
      setCreatedResult(result);
      onCreated(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Reservierung konnte nicht angelegt werden.");
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!createdResult) return;
    try {
      await navigator.clipboard?.writeText(createdResult.completion_url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={seed ? "Anfrage beantworten" : "Schnellbuchung"}
      description={
        seed
          ? "Zeitraum prüfen, dann geht dieselbe E-Mail wie bei der Schnellbuchung raus. Der Gast ergänzt fehlende Daten und leistet die Anzahlung."
          : "Nur die E-Mail ist Pflicht. Name, Anreiseart, Platzkategorie und Fahrzeuglänge trägt der Gast über den Link ein."
      }
      size="lg"
      footer={
        createdResult ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => resetForm(seed)}>
              {seed ? "Weitere Antwort" : "Weitere Schnellbuchung"}
            </Button>
            <Button type="button" onClick={onClose}>
              Schließen
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              form="phone-hold-form"
              loading={submitting}
              disabled={Boolean(seed?.placeBanned)}
              leftIcon={<Mail size={16} />}
            >
              Reservieren und E-Mail senden
            </Button>
          </div>
        )
      }
    >
      {createdResult ? (
        <SuccessPanel
          result={createdResult}
          email={email.trim()}
          categoryName={selectedCategory?.name ?? null}
          nights={nights}
          copied={copied}
          onCopy={() => void copyLink()}
        />
      ) : (
        <form id="phone-hold-form" className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
          <div className="flex gap-3 rounded-xl border border-accent/25 bg-accent/5 px-4 py-3">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
            <div className="space-y-2 text-sm leading-relaxed text-label-secondary">
              <p>
                <span className="font-medium text-label">Nur die E-Mail ist Pflicht.</span>{" "}
                Name, Anreiseart und Platzkategorie können leer bleiben. Der Gast trägt fehlende Angaben
                über den Link ein und leistet danach die Anzahlung, sofern Online-Zahlung eingerichtet ist.
              </p>
              {seed?.arrivalDate ? (
                <p>
                  Zeitraum aus der Mail: {formatIsoDateShort(seed.arrivalDate)} –{" "}
                  {formatIsoDateShort(seed.departureDate)}.
                </p>
              ) : null}
              {seed ? <p>{seed.depositHint}</p> : null}
            </div>
          </div>

          {seed?.guestId ? (
            <div className="rounded-xl border border-separator bg-fill-tertiary/40 px-4 py-3 text-sm">
              <p className="font-medium text-label">Daten des erkannten Gastes</p>
              {seed.placeBanned ? (
                <p className="mt-1 text-status-red">Platzverbot – es wird keine Reservierung angelegt.</p>
              ) : seed.missingLabels.length === 0 ? (
                <p className="mt-1 text-label-secondary">
                  Die Pflichtfelder sind hinterlegt. Im Link kann der Gast sie prüfen und die Anzahlung leisten.
                </p>
              ) : (
                <p className="mt-1 text-label-secondary">
                  Noch offen, der Gast trägt es im Link nach: {seed.missingLabels.join(", ")}.
                </p>
              )}
              {seed.presentLabels.length > 0 ? (
                <p className="mt-1 text-label-tertiary">Vorhanden: {seed.presentLabels.join(", ")}.</p>
              ) : null}
            </div>
          ) : seed ? (
            <p className="rounded-xl border border-separator bg-fill-tertiary/40 px-4 py-3 text-sm text-label-secondary">
              Kein Gast erkannt. Es wird ein neuer Gast mit dieser E-Mail angelegt. Die fehlenden Daten
              trägt er über den Link ein.
            </p>
          ) : null}

          <Section
            step="1"
            title="Zeitraum"
            hint={nights > 0 ? `${nights} ${nights === 1 ? "Nacht" : "Nächte"}` : undefined}
          >
            <BookingPeriodDateFields
              arrivalId="phone-hold-arrival"
              departureId="phone-hold-departure"
              arrival={arrivalDate}
              departure={departureDate}
              arrivalMin={todayLocal()}
              departureMin={addDaysLocal(arrivalDate, 1)}
              onArrivalChange={setArrivalDate}
              onDepartureChange={setDepartureDate}
            />
          </Section>

          <Section step="2" title="Gast">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Vorname" htmlFor="phone-hold-first-name">
                <input
                  id="phone-hold-first-name"
                  className={inputClass}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  placeholder="Optional"
                />
              </Field>
              <Field label="Nachname" htmlFor="phone-hold-last-name">
                <input
                  id="phone-hold-last-name"
                  className={inputClass}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  placeholder="Optional"
                />
              </Field>
            </div>
            {seed && (seed.adults != null || seed.children != null || seed.dogs != null) ? (
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <Field label="Erwachsene" htmlFor="phone-hold-adults">
                  <input
                    id="phone-hold-adults"
                    type="number"
                    min={1}
                    max={20}
                    className={inputClass}
                    value={adults}
                    onChange={(e) => setAdults(clampCount(e.target.value, 1, 20))}
                  />
                </Field>
                <Field label="Kinder" htmlFor="phone-hold-children">
                  <input
                    id="phone-hold-children"
                    type="number"
                    min={0}
                    max={20}
                    className={inputClass}
                    value={children}
                    onChange={(e) => setChildren(clampCount(e.target.value, 0, 20))}
                  />
                </Field>
                <Field label="Hunde" htmlFor="phone-hold-dogs">
                  <input
                    id="phone-hold-dogs"
                    type="number"
                    min={0}
                    max={10}
                    className={inputClass}
                    value={dogs}
                    onChange={(e) => setDogs(clampCount(e.target.value, 0, 10))}
                  />
                </Field>
              </div>
            ) : null}
            <Field label="E-Mail *" htmlFor="phone-hold-email" className="mt-3">
              <input
                id="phone-hold-email"
                type="email"
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="name@example.com"
              />
              <span className="mt-1 block text-xs text-label-secondary">
                An diese Adresse geht der Link für die restlichen Angaben.
              </span>
            </Field>
          </Section>

          <Section step="3" title="Platz">
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                Anreiseart
              </p>
              <p className="text-xs text-label-secondary">
                Optional. Die Fahrzeuglänge fragt der Gast beim Bestätigen ab.
              </p>
              {arrivalTypesLoading ? (
                <p className="text-sm text-label-secondary">Anreisearten werden geladen…</p>
              ) : arrivalTypesError ? (
                <p className="text-sm text-status-red">{arrivalTypesError}</p>
              ) : (
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Anreiseart">
                  {arrivalTypes.map((type) => {
                    const selected = arrivalType === type.code;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => {
                          setArrivalType((current) => (current === type.code ? "" : type.code));
                          setError(null);
                        }}
                        className={clsx(
                          "rounded-full border px-3 py-1.5 text-sm font-medium transition",
                          selected
                            ? "border-accent bg-accent text-label-on-accent"
                            : "border-separator bg-card text-label hover:border-accent/50",
                        )}
                      >
                        {type.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                Platzkategorie
              </p>
              <p className="text-xs text-label-secondary">
                Optional. Ohne Auswahl wählt der Gast die Kategorie beim Bestätigen. Der Stellplatz
                wird später automatisch zugewiesen.
              </p>
              {activeCategories.length === 0 ? (
                <p className="text-sm text-label-secondary">Keine aktive Platzkategorie vorhanden.</p>
              ) : (
                <ul className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Platzkategorie">
                  {activeCategories.map((category) => {
                    const selected = categoryId === category.id;
                    const color = category.color || DEFAULT_PITCH_CATEGORY_COLOR;
                    return (
                      <li key={category.id}>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          onClick={() => {
                            setCategoryId((current) => (current === category.id ? "" : category.id));
                            setError(null);
                          }}
                          className={clsx(
                            "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition",
                            selected
                              ? "border-accent bg-accent/10 ring-1 ring-accent"
                              : "border-separator bg-card hover:border-accent/40",
                          )}
                        >
                          <span
                            className="h-8 w-1.5 shrink-0 rounded-full"
                            style={{ background: color }}
                            aria-hidden
                          />
                          <span className="block truncate text-sm font-medium text-label">
                            {category.name}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {seed && seed.wantsElectricity != null ? (
              <label className="mt-4 flex items-center gap-2 text-sm text-label">
                <input
                  type="checkbox"
                  checked={wantsElectricity}
                  onChange={(e) => setWantsElectricity(e.target.checked)}
                />
                Stromanschluss
              </label>
            ) : null}

            <div className="mt-4 space-y-2 border-t border-separator pt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
                Platz bleibt reserviert
              </p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Reservierungsfrist">
                {HOLD_PRESETS.map((preset) => {
                  const selected = holdPreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => selectHoldPreset(preset.id)}
                      className={clsx(
                        "rounded-lg border px-3 py-1.5 text-sm transition",
                        selected
                          ? "border-accent bg-accent/10 font-medium text-label ring-1 ring-accent"
                          : "border-separator bg-card text-label hover:border-accent/40",
                      )}
                    >
                      {preset.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  role="radio"
                  aria-checked={holdPreset === "custom"}
                  onClick={() => selectHoldPreset("custom")}
                  className={clsx(
                    "rounded-lg border px-3 py-1.5 text-sm transition",
                    holdPreset === "custom"
                      ? "border-accent bg-accent/10 font-medium text-label ring-1 ring-accent"
                      : "border-separator bg-card text-label hover:border-accent/40",
                  )}
                >
                  Eigenes Datum
                </button>
              </div>
              {holdPreset === "custom" ? (
                <input
                  type="datetime-local"
                  className={inputClass}
                  value={holdUntilLocal}
                  onChange={(e) => setHoldUntilLocal(e.target.value)}
                  required
                  aria-label="Reserviert bis"
                />
              ) : (
                <p className="text-xs text-label-secondary">
                  Bis {formatDateTimeShort(localDateTimeToIsoSafe(holdUntilLocal))}. Ohne
                  Bestätigung wird der Platz danach wieder frei.
                </p>
              )}
            </div>
          </Section>

          {showNotes ? (
            <Field label="Notiz" htmlFor="phone-hold-notes">
              <textarea
                id="phone-hold-notes"
                className={inputClass}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Nur für die Rezeption, nicht für den Gast."
              />
            </Field>
          ) : (
            <button
              type="button"
              onClick={() => setShowNotes(true)}
              className="text-sm font-medium text-accent hover:underline"
            >
              Notiz für die Rezeption hinzufügen
            </button>
          )}

          {error ? (
            <p className="text-sm text-status-red" role="alert">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </Modal>
  );
}

function clampCount(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return min;
  return Math.min(max, Math.max(min, parsed));
}

function localDateTimeToIsoSafe(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return new Date().toISOString();
  return d.toISOString();
}

function Section({
  step,
  title,
  hint,
  children,
}: {
  step: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-separator bg-fill-tertiary/50 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-label">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[11px] font-semibold text-label-on-accent">
            {step}
          </span>
          {title}
        </h3>
        {hint ? <p className="text-xs font-medium text-label-secondary">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  htmlFor,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className={clsx("block space-y-1", className)}>
      <span className="text-sm text-label-secondary">{label}</span>
      {children}
    </label>
  );
}

function SuccessPanel({
  result,
  email,
  categoryName,
  nights,
  copied,
  onCopy,
}: {
  result: PhoneBookingHoldResult;
  email: string;
  categoryName: string | null;
  nights: number;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
          <Check className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-label">
            Reservierung {result.booking.booking_number} ist vorgemerkt
          </p>
          <p className="mt-1 text-sm leading-relaxed text-label-secondary">
            {result.email_sent
              ? `Die E-Mail an ${email} ist unterwegs. Darin steht der Link, über den der Gast die restlichen Angaben selbst einträgt.`
              : "Die E-Mail konnte nicht versendet werden. Den Link können Sie kopieren und selbst weitergeben."}
          </p>
        </div>
      </div>

      <dl className="grid gap-2 rounded-xl border border-separator bg-fill-tertiary/50 p-4 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-label-secondary">Zeitraum</dt>
          <dd className="mt-0.5 font-medium text-label">
            {nights > 0 ? `${nights} ${nights === 1 ? "Nacht" : "Nächte"}` : "—"}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-label-secondary">Kategorie</dt>
          <dd className="mt-0.5 font-medium text-label">{categoryName ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-label-secondary">Frist</dt>
          <dd className="mt-0.5 font-medium text-label">{formatDateTimeShort(result.hold_until)}</dd>
        </div>
      </dl>

      <div className="rounded-xl border border-separator bg-card px-3 py-2.5">
        <p className="text-xs text-label-secondary">Link für den Gast</p>
        <p className="mt-1 break-all text-sm text-label">{result.completion_url}</p>
      </div>
      <Button
        type="button"
        variant="secondary"
        leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
        onClick={onCopy}
      >
        {copied ? "Link kopiert" : "Link kopieren"}
      </Button>
    </div>
  );
}
